/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable complexity */
import React, { useState, useEffect, useCallback, useImperativeHandle, useMemo } from 'react';
import useInterval from 'react-scale/hooks/useInterval';
import {
  AuditResultV2,
  AuditResultV2Collection,
  PerTurnFeedback,
  TaskType,
} from '@scale/shared/defaults';
import {
  updateAttemptAudit,
  getAttemptAuditById,
  getComponentScoresByAttemptAuditId,
  updateComponentScoresForAttemptAuditId,
} from './ChatBulkAuditApi';
import {
  ChatBulkAuditBatch,
  SubtaskAssignment,
  SubtaskAssignmentAbbreviatedResponse,
  RubricAnswers,
} from '../SpeedAudit/types';
import {
  AttemptAuditCriterionScore,
  DEFAULT_ATTEMPT_AUDIT_SCORE,
  isCriterionComplete,
} from '@scale/shared/types/speedAudit';
import ChatBulkAuditErrorPicker from 'react-scale/components/audits/ChatBulkAudit/ChatBulkAuditErrorPicker';
import ChatBulkAuditTaskActionPicker from 'react-scale/components/audits/ChatBulkAudit/ChatBulkAuditTaskActionPicker';
import ChatBulkAuditCriteriaFeedbackSummary from 'react-scale/components/audits/ChatBulkAudit/ChatBulkAuditCriteriaFeedbackSummary';
import { TextInput } from 'design-system';
import { IProjectJson } from '@scale/shared/types/project';
import { IUserJson } from '@scale/shared/types/user';
import { StellarTaskDialog } from 'react-scale/components/audits/SpeedAudit/v2/StellarTaskDialog';
import { makeStyles } from 'react-scale/components/design-system';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ChatBulkAuditResultToggle } from 'react-scale/components/audits/ChatBulkAudit/ChatBulkAuditResultToggle';
import ChatBulkAuditTaskInfoBlock from 'react-scale/components/audits/ChatBulkAudit/ChatBulkAuditTaskInfoBlock';
import { ChatBulkAuditTaskAction } from '@scale/shared/types/corp/attemptAudit';
import { Button as ScaleUIButton, Checkbox, Typography, Icon as ScaleUIIcon } from '@scale/scaleui';
import { useSkipAuditAttemptsStore } from 'components/audits/ChatBulkAudit/hooks/useSkipAuditAttemptStore';
import { useAuditActions } from 'react-scale/components/ExpertManagement/stores/audit.store';
import {
  getAuditResultPageUrl,
  calculateOverallScoreFromComponentScores,
} from 'react-scale/components/audits/ChatBulkAudit/utils';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';
import ConfirmModal from './ConfirmModal';
import { FeedbackPerTurn } from './FeedbackPerTurn/FeedbackPerTurn';
import ChatBulkAuditReviewLevelPicker from 'react-scale/components/audits/ChatBulkAudit/ChatBulkAuditReviewLevelPicker';
import {
  useErrorCategoriesToMaxScoreMap,
  useAuditorPermissions,
} from 'react-scale/components/audits/ChatBulkAudit/apiClient';
import {
  getMaxScoreForErrorCategories,
  getScoreFromGrade,
  getGradeFromScore,
} from '@scale/shared/types/auditErrorCategories';
import { buildURL } from '@scale/frontend-lib/urls';
import { OUTLIER_FRONTEND_URL } from 'react-scale/lib/constants';
import {
  Button,
  Flex,
  Icon,
  Tooltip,
  Skeleton,
  Card,
  Text,
  HoverCard,
  Spinner,
} from '@scale/scaleui-radix';
import ReportSuspiciousContributorDialog from 'react-scale/corp/components/Lookup/UserLookup/ReportSuspiciousContributorDialog';
import { ReportingSource } from '@scale/shared/trustAndSafety/tnsAudits/types';
import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { useAuditNavigationShortcuts } from './hooks/useAuditNavigationShortcuts';
import { DISPUTE_AUDIT_BATCH_NAME } from 'react-scale/corp/views/CorpProjectGroupManager/views/Quality/Reviewers/ReviewerDisputesTable';
import { AuditRubric, AuditRubricCriterion } from '@scale/shared-types';
import { ChatResponseV2, isChatTaskType, ThreadedChatResponse } from '@scale/shared/types/chat';
import { useChatResponseCompletionStatusStore } from './hooks/useChatResponseCompletionStatusStore';
import { CriterionToFill } from 'react-scale/corp/views/CorpProjectGroupManager/views/Quality/DataQuality/AuditRubric/CriterionToFill';

interface ChatBulkAuditTaskFeedbackBarProps {
  // used for tracking pages on feedback
  rubric?: AuditRubric;
  auditBatch: ChatBulkAuditBatch;
  currentAttemptAudit: number;
  setCurrentAttemptAudit: (index: number) => void;
  // used for responses, cached layer
  subtaskAssignment?: SubtaskAssignment;
  subtaskAssignmentsLocalCopy: SubtaskAssignmentAbbreviatedResponse[];
  user: IUserJson;
  userHasProjectRole: boolean;
  userHasClaimTaskPerms: boolean;
  allowEdits?: boolean;
  auditorHasClaim?: boolean;
}

const useStyles = makeStyles(() => ({
  attemptSection: {
    borderBottom: '1px solid #E5E7EB',
  },
  containerSection: {
    padding: '8px 16px 12px 16px',
    marginTop: '0',
    width: '100%',
  },
  label: {
    fontWeight: 500,
  },
  warningMessage: {
    padding: '8px 16px 12px 16px',
  },
  taskSection: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
  },
  footer: {
    marginTop: 'auto',
    borderTop: '1px solid #E5E7EB',
  },
}));

const defaultRubricAnswers = (rubric?: AuditRubric) => {
  if (!rubric) {
    return {};
  }
  return Object.fromEntries(
    rubric?.criteria.map(criterion => [criterion.id, { criterionId: criterion.id }]) ?? [],
  );
};

// TODO: will make a follow on PR to simplify the update logic
// eslint-disable-next-line complexity
function ChatBulkAuditTaskFeedbackBar(props: ChatBulkAuditTaskFeedbackBarProps, ref: $FIXME) {
  const {
    rubric,
    auditBatch,
    currentAttemptAudit,
    subtaskAssignment,
    setCurrentAttemptAudit,
    subtaskAssignmentsLocalCopy,
    userHasClaimTaskPerms,
    allowEdits,
    auditorHasClaim,
  } = props;

  const ENABLE_EDIT_CONFIRM_MODAL = false;

  const projectId =
    (subtaskAssignment?.subtask?.project as IProjectJson)?._id?.toString() ??
    subtaskAssignment?.subtask?.project ??
    undefined;

  const classes = useStyles();

  const taskId = subtaskAssignment?.auditedAttempt?.task;
  const taskAttempt = useMemo(() => subtaskAssignment?.auditedAttempt, [subtaskAssignment]);
  const batchCompleted = auditBatch.completed;
  const attemptAuditId = auditBatch.attemptAudits[currentAttemptAudit];
  const subtaskAssignmentResult =
    subtaskAssignment?.resultV2 && subtaskAssignment?.resultV2.length > 0
      ? subtaskAssignment?.resultV2[0]
      : null;

  const completionStatusByAttemptAuditId = useChatResponseCompletionStatusStore(
    state => state.completionStatusByAttemptAuditId,
  );

  const [maxScoreDisputed, setMaxScoreDisputed] = useState<boolean>(
    subtaskAssignment?.maxScoreDisputed ?? false,
  );
  const [errorCategories, setErrorCategories] = useState<string[] | undefined>(
    subtaskAssignmentResult?.errorCategories,
  );

  const [perTurnFeedback, setPerTurnFeedback] = useState<PerTurnFeedback>(
    subtaskAssignmentResult?.perTurnFeedback ?? {},
  );

  // Rubric answers state - stores selected options, error categories, and justifications per criterion
  const [rubricAnswers, setRubricAnswers] = useState<RubricAnswers>(
    subtaskAssignment?.rubricAnswers ?? defaultRubricAnswers(rubric),
  );
  const onRubricAnswersChange = useCallback(
    (newRubricAnswers: RubricAnswers) => {
      setRubricAnswers(newRubricAnswers);
      if (rubric) {
        const calculatedCriteriaScore = calculateOverallScoreFromComponentScores({
          componentScores: Object.values(newRubricAnswers),
          rubric,
        });
        setGrade(getGradeFromScore(calculatedCriteriaScore ?? 0));
        setErrorCategories(
          Object.values(newRubricAnswers)
            .map(answer => answer.errorCategory)
            .filter(ec => ec !== undefined) as string[],
        );
      }
    },
    [rubric],
  );

  const [grade, setGrade] = useState<AuditResultV2 | null>(
    (subtaskAssignmentResult?.result as AuditResultV2) ?? rubric
      ? getGradeFromScore(
          calculateOverallScoreFromComponentScores({
            componentScores: Object.values(rubricAnswers),
            rubric: rubric!,
          }) ?? 0,
        )
      : null,
  );

  const { data: errorCategoriesToMaxScoreMap } = useErrorCategoriesToMaxScoreMap(projectId);
  const [maxScore, setMaxScore] = useState<number | undefined>(undefined);
  const [taskAction, setTaskAction] = useState<string | null>(
    subtaskAssignment?.auditAction ?? null,
  );
  const [sendToReviewLevel, setSendToReviewLevel] = useState<string | null>(
    subtaskAssignment?.sendToReviewLevel ?? null,
  );
  const [feedback, setFeedback] = useState<string | undefined>(subtaskAssignmentResult?.feedback);
  const [internalFeedback, setInternalFeedback] = useState<string | undefined>(
    subtaskAssignmentResult?.internalFeedback,
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showAutosavingMessage, setShowAutosavingMessage] = useState<boolean>(false);
  const [pageNumber, setPageNumber] = useState<string>((currentAttemptAudit + 1).toString());
  const [dirty, setDirty] = useState<boolean>(false);
  const [isHydratingAttemptAudit, setIsHydratingAttemptAudit] = useState<boolean>(false);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [pendingAction, setPendingAction] = useState<() => void>(() => {});
  const [showReportSuspiciousDialog, setShowReportSuspiciousDialog] = useState(false);
  const setOpenChatBulkAuditResults = useAuditActions().setOpenChatBulkAuditResults;

  const skipAuditAttemptIds = useSkipAuditAttemptsStore(state => state.skipAuditAttemptIds);

  const { data: auditorPermissions } = useAuditorPermissions();

  const allowAuditPerTurn = useUnleashFeatureFlag(
    'cba-audit-per-turn',
    {
      projectId,
    },
    false,
    !!projectId,
  );

  const enableClaimTaskViaFF = useUnleashFeatureFlag(
    'enable-claim-task-cba',
    {
      projectId,
    },
    false,
  );

  const enableStandardizedErrorCategories = useUnleashFeatureFlag(
    'enable-standardized-error-categories',
    { projectId },
    false,
  );

  const [showConfirmMaxScoreModal, setShowConfirmMaxScoreModal] = useState<boolean>(false);
  const [tempMaxScoreDisputed, setTempMaxScoreDisputed] = useState<boolean>(false);

  useEffect(() => {
    setDirty(true);
  }, [skipAuditAttemptIds]);

  useEffect(() => {
    if (errorCategoriesToMaxScoreMap) {
      setMaxScore(getMaxScoreForErrorCategories(errorCategories, errorCategoriesToMaxScoreMap));
    }
    if (grade && maxScore && getScoreFromGrade(grade) && !maxScoreDisputed) {
      if ((getScoreFromGrade(grade) ?? 0) > maxScore) {
        setGrade(getGradeFromScore(maxScore));
      }
    }
  }, [errorCategories, errorCategoriesToMaxScoreMap, grade, maxScore, maxScoreDisputed]);

  const setGradeAndUpdateCachedCopy = useCallback(
    (newGrade: AuditResultV2 | null, skipSetDirty: boolean = false) => {
      if (!newGrade) {
        return;
      }
      setGrade(newGrade);
      if (subtaskAssignmentResult) {
        subtaskAssignmentResult.result = newGrade;
      } else if (subtaskAssignment) {
        subtaskAssignment.resultV2 = [
          {
            attemptId: subtaskAssignment?.auditLookup.id,
            errorCategories,
            result: newGrade,
            feedback,
            perTurnFeedback,
          },
        ] as AuditResultV2Collection;
      }
      if (!skipSetDirty) {
        setDirty(true);
      }
    },
    [subtaskAssignment],
  );
  const setErrorCategoriesAndUpdateCachedCopy = useCallback(
    (newErrorCategories: string[], skipSetDirty: boolean = false) => {
      setErrorCategories(newErrorCategories);
      if (subtaskAssignmentResult) {
        subtaskAssignmentResult.errorCategories = newErrorCategories;
      } else if (subtaskAssignment) {
        subtaskAssignment.resultV2 = [
          {
            attemptId: subtaskAssignment.auditLookup.id,
            errorCategories: newErrorCategories,
            result: grade,
            feedback,
            perTurnFeedback,
          },
        ] as AuditResultV2Collection;
      }
      if (!skipSetDirty) {
        setDirty(true);
      }
    },
    [subtaskAssignment],
  );

  // TODO there is an important issue here, in React we should never update an object directly but using a state cause otherwise it messes up the rendering tree
  // As this is being like that in the whole file, I will leave it like that but it should be fixed in the future as it will probably cause bugs.
  // Also the dependecies for all these useCallbacks are not ok imo, they should be updated to use actual states and not the subtaskAssignment which is a plain object that has the issue I mentioned above.
  const setPerTurnFeedbackAndUpdateCachedCopy = useCallback(
    (newPerTurnFeedback: PerTurnFeedback, skipSetDirty: boolean = false) => {
      setPerTurnFeedback(newPerTurnFeedback);
      if (subtaskAssignmentResult) {
        subtaskAssignmentResult.perTurnFeedback = newPerTurnFeedback;
      } else if (subtaskAssignment) {
        subtaskAssignment.resultV2 = [
          {
            attemptId: subtaskAssignment.auditLookup.id,
            perTurnFeedback: newPerTurnFeedback,
            result: grade,
            feedback,
          },
        ] as AuditResultV2Collection;
      }
      if (!skipSetDirty) {
        setDirty(true);
      }
    },
    [subtaskAssignment],
  );

  const setFeedbackAndUpdateCachedCopy = useCallback(
    (newFeedback: string, skipSetDirty: boolean = false) => {
      setFeedback(newFeedback);
      if (subtaskAssignmentResult) {
        subtaskAssignmentResult.feedback = feedback;
      } else if (subtaskAssignment) {
        subtaskAssignment.resultV2 = [
          {
            attemptId: subtaskAssignment.auditLookup.id,
            errorCategories,
            result: grade,
            feedback: newFeedback,
            perTurnFeedback,
          },
        ] as AuditResultV2Collection;
      }
      if (!skipSetDirty) {
        setDirty(true);
      }
    },
    [subtaskAssignment],
  );
  const setTaskActionAndUpdateCachedCopy = useCallback(
    (newTaskAction: string | null, skipSetDirty: boolean = false) => {
      setTaskAction(newTaskAction);
      if (!newTaskAction) {
        return;
      }
      if (subtaskAssignment) {
        subtaskAssignment.auditAction = newTaskAction as ChatBulkAuditTaskAction;
      }
      if (!skipSetDirty) {
        setDirty(true);
      }
    },
    [subtaskAssignment],
  );

  const setTaskSendToReviewLevelAndUpdateCachedCopy = useCallback(
    (newSendToReviewLevel: string | null, skipSetDirty: boolean = false) => {
      setSendToReviewLevel(newSendToReviewLevel);
      if (!newSendToReviewLevel) {
        return;
      }
      if (subtaskAssignment) {
        subtaskAssignment.sendToReviewLevel = newSendToReviewLevel;
      }
      if (!skipSetDirty) {
        setDirty(true);
      }
    },
    [subtaskAssignment],
  );

  const setMaxScoreDisputedAndUpdateCachedCopy = useCallback(
    (newMaxScoreDisputed: boolean, skipSetDirty: boolean = false) => {
      setMaxScoreDisputed(newMaxScoreDisputed);
      if (subtaskAssignment) {
        subtaskAssignment.maxScoreDisputed = newMaxScoreDisputed;
      }
      if (!skipSetDirty) {
        setDirty(true);
      }
    },
    [subtaskAssignment],
  );

  const updateInternalFeedback = useCallback(
    (newInternalFeedback: string) => {
      setInternalFeedback(newInternalFeedback);
      if (subtaskAssignmentResult) {
        subtaskAssignmentResult.internalFeedback = newInternalFeedback;
      } else if (subtaskAssignment) {
        subtaskAssignment.resultV2 = [
          {
            attemptId: subtaskAssignment.auditLookup.id,
            errorCategories,
            result: grade,
            feedback,
            internalFeedback: newInternalFeedback,
            perTurnFeedback,
          },
        ] as AuditResultV2Collection;
      }
      setDirty(true);
    },
    [subtaskAssignment],
  );

  // Update rubric answers for a specific criterion and cache in subtaskAssignment
  const setRubricAnswersAndUpdateCachedCopy = useCallback(
    (newRubricAnswers: RubricAnswers, skipSetDirty: boolean = false) => {
      onRubricAnswersChange(newRubricAnswers);

      // Mutate the subtaskAssignment to cache (same pattern as other fields)
      if (subtaskAssignment) {
        subtaskAssignment.rubricAnswers = newRubricAnswers;
      }

      if (!skipSetDirty) {
        setDirty(true);
      }
    },
    [subtaskAssignment, onRubricAnswersChange],
  );

  // Update a single criterion's answer
  const updateRubricCriterionAnswer = useCallback(
    (criterionId: string, answer: AttemptAuditCriterionScore) => {
      const newRubricAnswers = {
        ...rubricAnswers,
        [criterionId]: answer,
      };
      setRubricAnswersAndUpdateCachedCopy(newRubricAnswers);
    },
    [rubricAnswers, setRubricAnswersAndUpdateCachedCopy],
  );

  const autosaveFeedback = useCallback(async () => {
    if (!dirty || auditBatch.completed) {
      return;
    }
    if (!attemptAuditId) {
      setErrorMessage("Can't autosave! No attempt audit ID. Please contact eng support");
      return;
    }
    try {
      setShowAutosavingMessage(true);
      const isFromDispute = auditBatch.name.includes(DISPUTE_AUDIT_BATCH_NAME);
      await updateAttemptAudit({
        attemptAuditId,
        grade: grade ?? undefined,
        errorCategories,
        feedback,
        internalFeedback,
        taskAction: taskAction ? (taskAction as ChatBulkAuditTaskAction) : undefined,
        ignored: skipAuditAttemptIds.has(subtaskAssignment?._id ?? ''),
        sendToReviewLevel: sendToReviewLevel ?? undefined,
        maxScoreDisputed,
        perTurnFeedback,
        isFromDispute,
      });

      // Save rubric answers to ComponentScores model (separate from AttemptAudit)
      if (Object.keys(rubricAnswers).length > 0) {
        await updateComponentScoresForAttemptAuditId(attemptAuditId, {
          criteriaScores: Object.values(rubricAnswers),
        });
      }
      setDirty(false);
    } catch (err) {
      setErrorMessage('Autosave failed: ' + err);
    } finally {
      setShowAutosavingMessage(false);
    }
  }, [
    dirty,
    attemptAuditId,
    errorCategories,
    grade,
    feedback,
    internalFeedback,
    taskAction,
    sendToReviewLevel,
    maxScoreDisputed,
    perTurnFeedback,
    rubricAnswers,
  ]);

  useImperativeHandle(ref, () => ({
    autosaveFeedback,
  }));

  const navigateToPage = (nextAttemptAudit: number) => {
    if (ENABLE_EDIT_CONFIRM_MODAL && (auditorHasClaim || allowEdits)) {
      setPendingAction(() => () => proceedToPage(nextAttemptAudit));
      setShowConfirmModal(true);
      return;
    }
    proceedToPage(nextAttemptAudit);
  };

  useAuditNavigationShortcuts(currentAttemptAudit, auditBatch.attemptAudits.length, navigateToPage);

  const proceedToPage = (nextAttemptAudit: number) => {
    if (nextAttemptAudit >= 0 && nextAttemptAudit < auditBatch.attemptAudits.length) {
      autosaveFeedback()
        .finally(() => {
          setCurrentAttemptAudit(nextAttemptAudit);
          setPageNumber((nextAttemptAudit + 1).toString());
          const nextCachedCopyAttemptAudit = subtaskAssignmentsLocalCopy[nextAttemptAudit];
          if (
            nextCachedCopyAttemptAudit.resultV2 &&
            nextCachedCopyAttemptAudit.resultV2.length > 0
          ) {
            setGrade(nextCachedCopyAttemptAudit.resultV2[0].result as AuditResultV2);
            setFeedback(nextCachedCopyAttemptAudit.resultV2[0].feedback);
            setErrorCategories(nextCachedCopyAttemptAudit.resultV2[0].errorCategories);
            setInternalFeedback(nextCachedCopyAttemptAudit.resultV2[0].internalFeedback);
          } else {
            setGrade(null);
            setErrorCategories(undefined);
            setFeedback(undefined);
            setInternalFeedback(undefined);
          }
          setTaskAction(nextCachedCopyAttemptAudit.auditAction ?? null);
          setMaxScoreDisputed(nextCachedCopyAttemptAudit.maxScoreDisputed ?? false);
          onRubricAnswersChange(
            nextCachedCopyAttemptAudit.rubricAnswers ?? defaultRubricAnswers(rubric),
          );
        })
        .catch(err => {
          // This should never actually happen, but adding the catch here to satisfy lint.
          throw err;
        });
    }
  };

  const handleSubmitAudit = () => {
    if (ENABLE_EDIT_CONFIRM_MODAL && (auditorHasClaim || allowEdits)) {
      setPendingAction(() => () => submitAudit());
      setShowConfirmModal(true);
      return;
    }
    submitAudit();
  };

  const submitAudit = () => {
    if (!window.location.pathname.includes('corp')) {
      autosaveFeedback()
        .then(() => {
          setOpenChatBulkAuditResults(true);
          const resultPageUrl = getAuditResultPageUrl(auditBatch._id);
          if (resultPageUrl) {
            window.location.href = resultPageUrl;
          }
          return;
        })
        .catch(() => {
          setErrorMessage("Failed to save feedback. Can't navigate away");
        });
    } else {
      const resultPageUrl = getAuditResultPageUrl(auditBatch._id);
      if (resultPageUrl) {
        autosaveFeedback()
          .then(() => {
            window.location.href = resultPageUrl;
            return;
          })
          .catch(() => {
            setErrorMessage("Failed to save feedback. Can't navigate away");
          });
      }
    }
  };

  const [start, stop] = useInterval(autosaveFeedback, 30000);

  // fetch the non-local copy just in case things are not synced
  useEffect(() => {
    start();

    return () => {
      stop();
    };
  }, [start, stop]);

  useEffect(() => {
    if (attemptAuditId) {
      setIsHydratingAttemptAudit(true);
      getAttemptAuditById(attemptAuditId)
        .then(newAttemptAudit => {
          setErrorMessage(null);
          setMaxScoreDisputedAndUpdateCachedCopy(newAttemptAudit.maxScoreDisputed ?? false, true);
          if (newAttemptAudit.resultV2 && newAttemptAudit.resultV2.length > 0) {
            setGradeAndUpdateCachedCopy(newAttemptAudit.resultV2[0].result as AuditResultV2, true);
            setErrorCategoriesAndUpdateCachedCopy(
              newAttemptAudit.resultV2[0].errorCategories,
              true,
            );
            setFeedbackAndUpdateCachedCopy(newAttemptAudit.resultV2[0].feedback ?? '', true);
            setInternalFeedback(newAttemptAudit.resultV2[0].internalFeedback ?? '');
            setPerTurnFeedbackAndUpdateCachedCopy(
              newAttemptAudit.resultV2[0].perTurnFeedback ?? {},
              true,
            );
          }
          setTaskActionAndUpdateCachedCopy(newAttemptAudit.auditAction ?? null, true);
          return;
        })
        .catch(err => {
          setErrorMessage(JSON.stringify(err.response.data));
        })
        .finally(() => {
          setPageNumber((currentAttemptAudit + 1).toString());
        })
        .catch(() => {
          // used to satisfy the linter
        });

      // Fetch rubric answers from ComponentScores model (separate from AttemptAudit)
      getComponentScoresByAttemptAuditId(attemptAuditId)
        .then(componentScores => {
          if (componentScores) {
            setRubricAnswersAndUpdateCachedCopy(
              {
                ...defaultRubricAnswers(rubric),
                ...Object.fromEntries(
                  componentScores.map(score => [
                    score.criterionId,
                    {
                      criterionId: score.criterionId,
                      rawValue: score.rawValue,
                      rawScore: score.rawScore,
                      justification: score.justification,
                      errorCategory: score.errorCategory,
                    },
                  ]),
                ),
              },
              true,
            );
            setIsHydratingAttemptAudit(false);
          }
          return;
        })
        .catch(() => {
          setIsHydratingAttemptAudit(false);
          setErrorMessage('Failed to fetch rubric answers');
        });
    }
  }, [
    attemptAuditId,
    currentAttemptAudit,
    setGradeAndUpdateCachedCopy,
    setErrorCategoriesAndUpdateCachedCopy,
    setPerTurnFeedbackAndUpdateCachedCopy,
    setFeedbackAndUpdateCachedCopy,
    setRubricAnswersAndUpdateCachedCopy,
    setMaxScoreDisputedAndUpdateCachedCopy,
    setTaskActionAndUpdateCachedCopy,
    rubric,
  ]);

  useEffect(() => {
    const handleNavigationAway = (event: BeforeUnloadEvent) => {
      if (dirty && !auditBatch.completed) {
        // eslint-disable promise/always-return Ignore for beforeunload event handling
        autosaveFeedback();
        event.preventDefault();
        event.returnValue = ''; // Show warning message before navigating away
      }
    };
    window.addEventListener('beforeunload', handleNavigationAway);

    return () => {
      window.removeEventListener('beforeunload', handleNavigationAway);
    };
  }, [dirty, autosaveFeedback]);

  const updateGrade = (newGrade: AuditResultV2 | null) => {
    if (!newGrade) return;
    setGradeAndUpdateCachedCopy(newGrade);
  };

  const updateErrorCategories = (newErrorCategories: string[]) => {
    setErrorCategoriesAndUpdateCachedCopy(newErrorCategories);
  };

  const updatePerTurnFeedback = (newPerTurnFeedback: PerTurnFeedback) => {
    setPerTurnFeedbackAndUpdateCachedCopy(newPerTurnFeedback);
  };

  const updateFeedback = (newFeedback: string) => {
    setFeedbackAndUpdateCachedCopy(newFeedback);
  };

  const updateTaskAction = (newTaskAction: string | null) => {
    setTaskActionAndUpdateCachedCopy(newTaskAction);
  };

  const updateTaskSendToReviewLevel = (newSendToReviewLevel: string | null) => {
    setTaskSendToReviewLevelAndUpdateCachedCopy(newSendToReviewLevel);
  };

  const updateMaxScoreDisputed = (newMaxScoreDisputed: boolean) => {
    if (newMaxScoreDisputed && !maxScoreDisputed) {
      setTempMaxScoreDisputed(newMaxScoreDisputed);
      setShowConfirmMaxScoreModal(true);
    } else {
      setMaxScoreDisputedAndUpdateCachedCopy(newMaxScoreDisputed);
    }
  };

  // disable task if not loaded or task is corrupted
  const disableFeedback =
    batchCompleted || !subtaskAssignment || skipAuditAttemptIds.has(subtaskAssignment?._id ?? '');

  const showRubric = !!rubric && !!taskAttempt && !!isChatTaskType(taskAttempt.taskType);
  const isCriterionAnswered = useCallback(
    (criterion: AuditRubricCriterion) => {
      return !!rubricAnswers[criterion.id]?.rawValue;
    },
    [rubricAnswers],
  );

  const reviewAndCompleteTooltip = useMemo(() => {
    if (
      rubric?.criteria.some(
        criterion => !isCriterionComplete(criterion, rubricAnswers[criterion.id]),
      )
    ) {
      return 'Some criteria are incomplete. You will effectively be skipping this task.';
    }
    for (let i = 0; i < auditBatch.attemptAudits.length; i++) {
      if (i === currentAttemptAudit) continue;
      if (
        rubric?.criteria.some(
          criterion =>
            !isCriterionComplete(
              criterion,
              subtaskAssignmentsLocalCopy[i].rubricAnswers?.[criterion.id] ?? {
                criterionId: criterion.id,
              },
            ),
        )
      ) {
        return `Some criteria of task ${
          i + 1
        } incomplete. You will effectively be skipping that task.`;
      }
    }
  }, [
    rubric,
    rubricAnswers,
    subtaskAssignmentsLocalCopy,
    currentAttemptAudit,
    auditBatch.attemptAudits.length,
  ]);
  const renderReviewAndCompleteButton = () => {
    const button = (
      <ScaleUIButton
        className="whitespace-nowrap"
        size="sm"
        variant="primary"
        onClick={handleSubmitAudit}
      >
        {auditBatch.completed ? 'View Audit Summary' : 'Review and Complete'}
      </ScaleUIButton>
    );
    if (showRubric && !!reviewAndCompleteTooltip) {
      return <Tooltip content={reviewAndCompleteTooltip}>{button}</Tooltip>;
    }
    return button;
  };

  const isLoadingTaskAttempt = !taskAttempt || !subtaskAssignment;
  if (isLoadingTaskAttempt || isHydratingAttemptAudit) {
    return (
      <Flex justify="center">
        <Skeleton />
      </Flex>
    );
  }

  // TODO: SpeedAuditErrorPicker to not wrap "Error type" text and have errors on bottom of component
  return (
    <div
      className="bg-neutral-0 py-3 items-center border-b border-neutral-200"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '8px',
        height: '90vh',
        overflowY: 'auto',
      }}
    >
      <div className={classes.containerSection + ' ' + classes.attemptSection}>
        {/* Use the local copy to enable faster switching on audit navigation */}
        {subtaskAssignment && (
          <ChatBulkAuditTaskInfoBlock auditBatch={auditBatch} attemptAudit={subtaskAssignment} />
        )}
      </div>

      {showRubric ? (
        <div className={classes.containerSection}>
          <Flex direction="column" gap="2" width="100%">
            <Card>
              <Flex direction="row" justify="between" width="100%">
                <Flex direction="row" gap="2" width="100%">
                  <Text className={classes.label}>Task Score</Text>
                  <HoverCard.Root>
                    <HoverCard.Trigger>
                      <Icon icon={faInfoCircle} label="Info" color="neutral" />
                    </HoverCard.Trigger>
                    <HoverCard.Content>
                      <Text>The overall score based on all criteria ratings.</Text>
                    </HoverCard.Content>
                  </HoverCard.Root>
                </Flex>
                <Text>
                  {grade && grade !== AuditResultV2.Undecided
                    ? getScoreFromGrade(grade)
                    : AuditResultV2.Undecided}
                </Text>
              </Flex>
            </Card>
            {rubric.criteria.map(criterion => (
              <CriterionToFill
                key={`${criterion.id}-${currentAttemptAudit}`}
                projectId={projectId}
                criterion={criterion}
                response={{
                  type: taskAttempt.taskType,
                  response:
                    taskAttempt.taskType === TaskType.Chat
                      ? (taskAttempt.response as ThreadedChatResponse)
                      : (taskAttempt.response as ChatResponseV2),
                }}
                criterionAnswer={rubricAnswers[criterion.id] ?? { criterionId: criterion.id }}
                onCriterionAnswerChange={answer =>
                  updateRubricCriterionAnswer(criterion.id, answer)
                }
                isAnswered={isCriterionAnswered(criterion)}
                isComplete={isCriterionComplete(criterion, rubricAnswers[criterion.id])}
              />
            ))}
          </Flex>
        </div>
      ) : (
        <>
          <div className={classes.containerSection}>
            <p className={classes.label}>How would you rate this task?</p>
            <ChatBulkAuditResultToggle
              value={grade}
              onChange={updateGrade}
              disabled={disableFeedback}
            />
            {maxScore !== null && maxScore !== undefined && enableStandardizedErrorCategories && (
              <>
                <p className={classes.label} style={{ color: 'red', marginTop: '8px' }}>
                  Max Score For Selected Error Categories is {maxScore}
                </p>
                <Checkbox
                  checked={maxScoreDisputed}
                  label="Override Max Score"
                  onChange={() => {
                    updateMaxScoreDisputed(!maxScoreDisputed);
                  }}
                />
              </>
            )}
          </div>
          <div className={classes.containerSection}>
            <p className={classes.label}>Error Categories</p>
            <ChatBulkAuditErrorPicker
              enableCreateNewError={false}
              selectedErrorCategories={errorCategories ?? []}
              projectId={projectId}
              toggleChoose={updateErrorCategories}
              isDisabled={disableFeedback}
            />
          </div>
        </>
      )}
      <div className={classes.containerSection}>
        <div className="flex items-center">
          <p className={classes.label}>Task Feedback for Contributor (External)</p>
          <ScaleUIIcon
            icon="info-circle"
            size="sm"
            tooltip="Persisted in attemptaudit.resultV2.feedback and sent to the contributor in the feedback tab on outlier"
            className="ml-1"
          />
        </div>
        <TextInput
          placeholder="Add descriptive feedback about this specific task"
          className="w-full max-h-44 overflow-y-auto"
          multiline
          disabled={disableFeedback}
          value={feedback ?? ''}
          style={{ width: '100%' }}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            updateFeedback((event.target as HTMLInputElement).value);
          }}
        />
      </div>

      <div className={classes.containerSection}>
        <div className="flex items-center">
          <p className={classes.label}>Overall Task Feedback (Internal)</p>
          <ScaleUIIcon
            icon="info-circle"
            size="sm"
            tooltip="Internal feedback. Not shared with contributors."
            className="ml-1"
          />
        </div>
        <Flex direction="column" gap="2">
          {showRubric && (
            <ChatBulkAuditCriteriaFeedbackSummary rubric={rubric} rubricAnswers={rubricAnswers} />
          )}
          <TextInput
            placeholder="Add internal feedback about this task (not visible to contributors)"
            className="w-full max-h-44 overflow-y-auto"
            multiline
            disabled={disableFeedback}
            value={internalFeedback ?? ''}
            style={{ width: '100%' }}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              updateInternalFeedback((event.target as HTMLInputElement).value);
            }}
          />
        </Flex>
      </div>
      {auditorPermissions?.hasTaskActionsPermissions && (
        <div className={classes.containerSection}>
          <Flex align="center" justify="start" gap="1">
            <p className={classes.label}>Task Action</p>
            <ScaleUIIcon
              icon="info-circle"
              size="sm"
              tooltip={
                <Flex direction="column" gap="1">
                  <p>- Actions will be performed after the audit is submitted</p>
                  <p>
                    - If this action collides with a bulk action, the bulk action will take
                    precedence
                  </p>
                  <p>
                    - If you want more advanced options like sending back to original attempter, do
                    not use this task action, instead, submit the audit and perform the action on
                    compass
                  </p>
                </Flex>
              }
            />
          </Flex>
          <ChatBulkAuditTaskActionPicker
            selectedTaskAction={
              subtaskAssignment && subtaskAssignment.edited
                ? ChatBulkAuditTaskAction.MOVE_FORWARD_ONE_LAYER
                : taskAction ?? ''
            }
            toggleChoose={updateTaskAction}
            isDisabled={disableFeedback || (subtaskAssignment && subtaskAssignment.edited)}
          />
          {taskAction === ChatBulkAuditTaskAction.SEND_BACK_TO_QUEUE_KEEP_RESPONSES && (
            <div className={classes.containerSection}>
              <p className={classes.label}>Send to Review Level</p>
              <ChatBulkAuditReviewLevelPicker
                selectedReviewLevel={
                  subtaskAssignment?.sendToReviewLevel ?? sendToReviewLevel ?? ''
                }
                toggleChoose={updateTaskSendToReviewLevel}
                isDisabled={disableFeedback}
              />
            </div>
          )}
        </div>
      )}
      {auditorPermissions?.hasTaskActionsPermissions &&
        subtaskAssignment &&
        subtaskAssignment.edited && (
          <div className={classes.containerSection}>
            <Typography.Subtitle3>
              This task will be moved forward by one review level since it has been edited.
            </Typography.Subtitle3>
          </div>
        )}
      {subtaskAssignment && !disableFeedback && (
        <Flex wrap="wrap" gap="2" px="4">
          <StellarTaskDialog
            feedback={feedback ?? ''}
            setFeedback={setFeedbackAndUpdateCachedCopy}
            taskAttemptId={subtaskAssignment.auditLookup.id}
            reviewedByUserId={props.user._id}
            dontDisableOnEmptyFeedback={true}
          />
          <Button
            variant="outline"
            onClick={() => {
              setShowReportSuspiciousDialog(true);
            }}
          >
            <Icon label="report-suspicious-contributor" icon={faCircleExclamation} />
            Report Cheating or Spam
          </Button>
        </Flex>
      )}
      <div className={classes.containerSection}>
        {taskId && (userHasClaimTaskPerms || enableClaimTaskViaFF) && (
          <ScaleUIButton
            onClick={() =>
              window.open(
                buildURL(OUTLIER_FRONTEND_URL, '/tasks', { [`taskId`]: taskId, forceClaim: 1 }),
                '_blank',
              )
            }
          >
            Claim and View Task on Outlier
          </ScaleUIButton>
        )}
      </div>
      {skipAuditAttemptIds.has(subtaskAssignment?._id ?? '') && (
        <div className={classes.containerSection}>
          <Checkbox
            checked={skipAuditAttemptIds.has(subtaskAssignment?._id ?? '')}
            label="Skip task (due to task corruption)"
            disabled={true}
            variant="primary"
          />
        </div>
      )}
      {allowAuditPerTurn && !showRubric && (
        <FeedbackPerTurn
          updatePerTurnFeedback={updatePerTurnFeedback}
          perTurnFeedback={perTurnFeedback ?? {}}
          classes={classes}
          projectId={projectId}
          disableFeedback={disableFeedback}
        />
      )}
      {errorMessage && <p className={classes.warningMessage}>{errorMessage}</p>}
      {completionStatusByAttemptAuditId[attemptAuditId] === false && (
        <div
          className={classes.warningMessage}
          style={{ backgroundColor: '#FEF3C7', border: '1px solid #F59E0B' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FontAwesomeIcon icon="exclamation-triangle" style={{ color: '#F59E0B' }} />
            <div>
              <p className={classes.label} style={{ marginBottom: '4px' }}>
                Task is not completed
              </p>
              <p style={{ fontSize: '14px', color: '#92400E' }}>
                You must finish all steps within the task before submitting feedback. Submitting
                feedback on an incomplete task may cause issues with task processing (go to the end
                and see if you might need to resubmit some steps).
              </p>
            </div>
          </div>
        </div>
      )}
      {rubric ? (
        <Flex wrap="wrap" gap="2" px="4" justify="end" width="100%">
          <Button
            variant="outline"
            disabled={!dirty || showAutosavingMessage}
            onClick={autosaveFeedback}
          >
            {showAutosavingMessage ? 'Autosaving…' : 'Save'}
            {showAutosavingMessage && <Spinner size="1" />}
          </Button>
        </Flex>
      ) : (
        showAutosavingMessage && <p className={classes.warningMessage}>Autosaving…</p>
      )}
      <div className={`${classes.footer} flex justify-between items-center w-full`}>
        <div className={'flex items-center gap-2 pt-4'}>
          <div className={classes.taskSection}>
            <Tooltip content={`Navigate to the previous task with "Cmd Shift -"`}>
              <button
                className={`bg-white hover:bg-gray-100 text-gray-700 font-bold py-2 px-4 rounded ${
                  currentAttemptAudit === 0 ? 'opacity-50 cursor-not-allowed' : 'disabled-button'
                }`}
                onClick={() => navigateToPage(currentAttemptAudit - 1)}
                disabled={currentAttemptAudit === 0}
              >
                <FontAwesomeIcon icon="chevron-left" />
              </button>
            </Tooltip>
            Task
            <TextInput
              value={pageNumber}
              onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                if (event.key === 'Enter') {
                  const newPageNumber = parseInt((event.target as HTMLInputElement).value);
                  if (!isNaN(newPageNumber) && newPageNumber - 1 !== currentAttemptAudit) {
                    navigateToPage(newPageNumber - 1);
                  } else {
                    setErrorMessage('Please enter a valid page number');
                  }
                }
              }}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setPageNumber(event.target.value)
              }
              style={{ width: '6ch', marginLeft: '4px', marginRight: '4px', borderRadius: '4px' }}
            />
            of {auditBatch.attemptAudits.length}
            <Tooltip content={`Navigate to the next task with "Cmd Shift +"`}>
              <button
                className={`bg-white hover:bg-gray-100 text-gray-700 font-bold py-2 px-4 rounded ${
                  currentAttemptAudit === auditBatch.attemptAudits.length - 1
                    ? 'opacity-50 cursor-not-allowed'
                    : ''
                }`}
                onClick={() => navigateToPage(currentAttemptAudit + 1)}
                disabled={currentAttemptAudit === auditBatch.attemptAudits.length - 1}
              >
                <FontAwesomeIcon icon="chevron-right" />
              </button>
            </Tooltip>
          </div>
          {renderReviewAndCompleteButton()}
        </div>
      </div>
      {showConfirmModal && (
        <ConfirmModal
          isOpen={showConfirmModal}
          onClose={() => setShowConfirmModal(false)}
          onConfirm={() => {
            setShowConfirmModal(false);
            pendingAction();
          }}
          message="If you have made edits to the task that you wish to keep, please remember to save them before navigating away. Are you sure you want to navigate away?"
        />
      )}
      {showConfirmMaxScoreModal && (
        <ConfirmModal
          isOpen={showConfirmMaxScoreModal}
          onClose={() => setShowConfirmMaxScoreModal(false)}
          onConfirm={() => {
            setShowConfirmMaxScoreModal(false);
            setMaxScoreDisputedAndUpdateCachedCopy(tempMaxScoreDisputed);
          }}
          message="The max score is determined based on the selected error categories. Are you sure you want to override the max score? This action will be recorded in the attempt audit."
        />
      )}
      {showReportSuspiciousDialog && taskAttempt && (
        <ReportSuspiciousContributorDialog
          isOpen={showReportSuspiciousDialog}
          onOpenChange={setShowReportSuspiciousDialog}
          contributorId={taskAttempt.attemptedBy}
          presetSuspiciousTaskAttemptIds={[taskAttempt._id]}
          showFraudCategories={false}
          sourceDetails={{
            type: 'internal_escalation',
            reportingSource: ReportingSource.ChatBulkAudit,
          }}
        />
      )}
    </div>
  );
}

export default React.memo(React.forwardRef(ChatBulkAuditTaskFeedbackBar));
